import React, { useContext } from 'react';
import { Handle, Position } from '@xyflow/react';
import {
  AccountBalanceOutlined,
  LocationCity,
  Map,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from '@mui/icons-material';
import {
  Typography,
  IconButton,
  Box,
  Paper,
  Divider,
  Tooltip,
  Chip,
  useTheme,
  alpha,
  Stack,
} from '@mui/material';
import { OrgSettingsContext } from '../../../../../contexts/org-settings-context';
import { IDeliveryChargeRule, DeliveryChargeRuleScope } from '../interfaces';
import { getGovernorateForDistrict } from '../../../../../data/regions';
import { highlightZoneFunc } from '../delivery-charge-overrides';
import Decimal from 'decimal.js';
import AmountChip from './amount-chip';
import { add, CURRENCY, displayMoney } from '../../../../../utils/money';
import { MerchantType } from '../../../../../interfaces/delivery-company/merchants';

interface IGovernorateNodeData {
  data: {
    name: string;
    rules: IDeliveryChargeRule[];
    highlightZone: highlightZoneFunc;
    expanded: boolean;
    toggleExpand: () => void;
    dimmed: boolean
    editRule: (r:IDeliveryChargeRule)=>void
    merchant: MerchantType
    readonly?: boolean
  };
}

export const GovernorateNode = ({ data }: IGovernorateNodeData) => {
  const { color } = useContext(OrgSettingsContext);
  const theme = useTheme();
  const primaryColor = color || theme.palette.primary.main;

  const governorateRule = data.rules.find(
    (item) =>
      item.scope === DeliveryChargeRuleScope.GOVERNORATE &&
      item.governorate === data.name
  );

  const districtRuleCount = data.rules.filter(
    (item) =>
      item.scope === DeliveryChargeRuleScope.DISTRICT &&
      getGovernorateForDistrict(item.district!) === data.name
  ).length;

  const areaRuleCount = data.rules.filter(
    (item) =>
      item.scope === DeliveryChargeRuleScope.AREA && getGovernorateForDistrict(item.area!.district) == data.name
  ).length;

  return (
    <Paper
      elevation={3}
        sx={{
            width: 360,
            borderRadius: 3,
            visibility: data.dimmed ? 'hidden' : 'visible',
            opacity: data.dimmed ? 0.01 : 1,
            filter: data.dimmed ? 'blur(1px)' : 'none',
            background: theme.palette.background.paper,
            border: `3px solid ${alpha(primaryColor, 0.3)}`,
            boxShadow: data.expanded ? `0 0 25px ${alpha(primaryColor, 1)}` : undefined,
            fontFamily: theme.typography.fontFamily,
            transition: 'all 0.3s ease',
            '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: alpha(primaryColor, 0.6),
            },
        }}

      onMouseEnter={() =>
        data.highlightZone(DeliveryChargeRuleScope.GOVERNORATE, data.name)
      }
      onMouseLeave={() =>
        data.highlightZone(DeliveryChargeRuleScope.GOVERNORATE, '')
      }
    >
      {/* Header - Extra large and bold */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        px={1.5}
        py={0}
        sx={{
          background: alpha(primaryColor, 0.15),
          borderTopLeftRadius: 'inherit',
          borderTopRightRadius: 'inherit',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <AccountBalanceOutlined
            fontSize="large"
            sx={{ 
              color: primaryColor,
              fontSize: '2rem' // Very large icon
            }}
          />
          <Typography
            variant="h4" // Extra large heading
            fontWeight={800} // Extra bold
            noWrap
            sx={{ 
              color: theme.palette.text.primary,
              fontSize: '1.8rem', // Explicit large size
              lineHeight: 1.2
            }}
          >
            {data.name}
          </Typography>
        </Stack>
        

          <IconButton
            onClick={data.toggleExpand}
            size="large" // Extra large button
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: primaryColor,
                background: alpha(primaryColor, 0.15),
              },
              padding: 1.5,
            }}
          >
            {data.expanded ? 
              <KeyboardArrowUp fontSize="large" /> : 
              <KeyboardArrowDown fontSize="large" />
            }
          </IconButton>
      </Stack>

      <Divider sx={{ 
        borderColor: alpha(theme.palette.divider, 0.2),
        borderWidth: 2 
      }} />

      {/* Content - Very large and clear */}
      <Box px={1.5} py={1.5}>
        {/* Governorate Rule - Very prominent */}
        <Box mb={1}>
            
          <AmountChip
            deltaCharge={governorateRule?.delta_charge}
            readonly={data.readonly}
            onClick={()=>{
                const rule = data.rules.find(rule => rule.scope == DeliveryChargeRuleScope.GOVERNORATE && rule.governorate == data.name)
                if (rule) 
                    data.editRule(rule);
                else
                    data.editRule({
                        scope: DeliveryChargeRuleScope.GOVERNORATE,
                        governorate: data.name,
                        delta_charge: new Decimal(0)
                    })
            }}
          />

           <Box mt={1}>Total Fee <b>{displayMoney(add(data.merchant.default_delivery_fee, governorateRule ? [[CURRENCY.USD, governorateRule!.delta_charge!]] : []))}</b></Box>
        </Box>

        {/* Rule Counts - Extra large and clear */}
        <Stack spacing={0}>
          <Box display="flex" alignItems="center">
            <LocationCity
              sx={{
                color: theme.palette.text.secondary,
                mr: 2,
                fontSize: '2rem' // Very large icon
              }}
            />
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                fontSize: '1.3rem',
                display: 'flex',
                alignItems: 'baseline'
              }}
            >
              <Box 
                component="span" 
                fontWeight={700} 
                color="text.primary"
                sx={{ 
                  fontSize: '1.6rem', // Extra large number
                  mr: 1.5,
                  minWidth: '1.5em',
                  display: 'inline-block',
                  textAlign: 'center'
                }}
              >
                {districtRuleCount}
              </Box>
              District Rules
            </Typography>
          </Box>

          <Box display="flex" alignItems="center">
            <LocationCity
              sx={{
                color: theme.palette.text.secondary,
                mr: 2,
                fontSize: '2rem' // Very large icon
              }}
            />
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                fontSize: '1.3rem',
                display: 'flex',
                alignItems: 'baseline'
              }}
            >
              <Box 
                component="span" 
                fontWeight={700} 
                color="text.primary"
                sx={{ 
                  fontSize: '1.6rem', // Extra large number
                  mr: 1.5,
                  minWidth: '1.5em',
                  display: 'inline-block',
                  textAlign: 'center'
                }}
              >
                {areaRuleCount}
              </Box>
              City Exceptions
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* React Flow Handles */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-source"
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          borderRadius: '50%',
          border: '2px solid white',
          bottom: -5,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      />
    </Paper>
  );
};